import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { X, UserPlus, Search, Loader2 } from "lucide-react";
import API from "@/lib/api";
import { getErrorMessage } from "@/lib/errorHandler";
import { useOrganizationContext } from "@/contexts/OrganizationContext";

// Reusable picker for direct per-user assignment on a quiz or sequence.
// Handles its own state (fetch on mount, search debounce, batch add, remove).
// The parent gets an onChange callback to invalidate cached lists after any
// mutation.
//
// props:
//   resourceType   'quiz' | 'sequence'
//   resourceId     UUID of the quiz or sequence
//   onChange?      Called after any successful assign/unassign
export default function AssignedUsersPicker({ resourceType, resourceId, onChange }) {
  const basePath =
    resourceType === "sequence" ? `/sequences/${resourceId}`
    : resourceType === "course" ? `/courses/${resourceId}`
    : `/quizzes/${resourceId}`;
  const { selectedOrg, isSuperadmin } = useOrganizationContext();

  const [loading, setLoading] = useState(true);
  const [assigned, setAssigned] = useState([]);
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(new Set()); // user_ids the admin picked in this session
  const [posting, setPosting] = useState(false);
  const [inviting, setInviting] = useState(false);

  const searchTimer = useRef(null);

  // ── Load current assignments ────────────────────────────────────────────
  const refreshAssigned = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(`${basePath}/assigned-users`);
      setAssigned(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load assigned users"));
      setAssigned([]);
    } finally {
      setLoading(false);
    }
  }, [basePath]);

  useEffect(() => {
    if (!resourceId) return;
    refreshAssigned();
  }, [resourceId, refreshAssigned]);

  // ── Debounced candidate search ──────────────────────────────────────────
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = search.trim();
    if (!q) {
      setCandidates([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const params = { search: q, limit: 25 };
        if (isSuperadmin && selectedOrg?.id) params.organization_id = selectedOrg.id;
        const res = await API.get("/user-management/students", { params });
        setCandidates(Array.isArray(res.data?.items) ? res.data.items : []);
      } catch (err) {
        toast.error(getErrorMessage(err, "Search failed"));
        setCandidates([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [search, isSuperadmin, selectedOrg?.id]);

  const assignedIds = new Set(assigned.map((a) => a.user_id));
  // Show only students not yet assigned + not empty search
  const visibleCandidates = candidates.filter((c) => !assignedIds.has(c.id));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const commitAdd = async () => {
    if (selected.size === 0) return;
    setPosting(true);
    try {
      const res = await API.post(`${basePath}/assign-users`, {
        user_ids: Array.from(selected),
      });
      const addedCount = res.data?.added?.length ?? 0;
      const skippedCount = res.data?.already_assigned?.length ?? 0;
      if (addedCount) toast.success(`Added ${addedCount} participant(s)`);
      if (skippedCount)
        toast.info(`${skippedCount} already assigned — skipped`);
      setSelected(new Set());
      setSearch("");
      setCandidates([]);
      await refreshAssigned();
      onChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to assign participants"));
    } finally {
      setPosting(false);
    }
  };

  // Pre-provision a participant account for an unknown email, then assign
  // them to this resource in the same gesture. The backend invite endpoint is
  // idempotent (existing participant in this org → same row back) and emails
  // brand-new accounts a portal invite.
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(search.trim());
  const inviteAndAssign = async () => {
    const email = search.trim().toLowerCase();
    setInviting(true);
    try {
      const invitePayload = { email };
      if (isSuperadmin && selectedOrg?.id) invitePayload.organization_id = selectedOrg.id;
      const invited = (await API.post("/user-management/students/invite", invitePayload)).data;
      await API.post(`${basePath}/assign-users`, { user_ids: [invited.id] });
      toast.success(
        invited.created
          ? `Invited and assigned ${invited.email} — they've been emailed a portal invite`
          : `Assigned existing participant ${invited.email}`
      );
      setSearch("");
      setCandidates([]);
      await refreshAssigned();
      onChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to invite participant"));
    } finally {
      setInviting(false);
    }
  };

  const removeOne = async (userId) => {
    try {
      await API.delete(`${basePath}/unassign-user/${userId}`);
      toast.success("Removed");
      await refreshAssigned();
      onChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to remove participant"));
    }
  };

  return (
    <div className="space-y-3">
      {/* ── Currently assigned ─────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="text-sm font-semibold text-slate-700">
          Assigned Participants {assigned.length > 0 && (
            <span className="text-slate-400 font-normal">({assigned.length})</span>
          )}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : assigned.length === 0 ? (
          <p className="text-xs text-slate-400 py-2">
            No participants assigned directly. Group assignments and email-domain
            rules still apply.
          </p>
        ) : (
          <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
            {assigned.map((a) => (
              <div
                key={a.user_id}
                className="flex items-center justify-between px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-emerald-900 truncate">
                    {a.name || a.email}
                  </p>
                  <p className="text-xs text-emerald-700 truncate">{a.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => removeOne(a.user_id)}
                  className="text-red-600 hover:bg-red-50 rounded p-1"
                  title="Remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Search + picker ─────────────────────────────────────────────── */}
      <div className="space-y-2 pt-2 border-t border-slate-100">
        <div className="text-sm font-semibold text-slate-700">Add Participants</div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name"
            className="w-full h-9 pl-9 pr-3 text-sm border border-slate-300 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 outline-none"
          />
          {searching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 animate-spin" />
          )}
        </div>

        {search.trim() && !searching && visibleCandidates.length === 0 && (
          looksLikeEmail ? (
            <button
              type="button"
              onClick={inviteAndAssign}
              disabled={inviting}
              className="w-full inline-flex items-center justify-center gap-1.5 h-9 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
            >
              {inviting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Invite {search.trim().toLowerCase()} &amp; assign
                </>
              )}
            </button>
          ) : (
            <p className="text-xs text-slate-400">
              No participants match. Type their full email to invite a new
              participant.
            </p>
          )
        )}

        {visibleCandidates.length > 0 && (
          <div className="max-h-48 overflow-y-auto space-y-1 pr-1 border border-slate-200 rounded-lg p-1">
            {visibleCandidates.map((c) => {
              const isSelected = selected.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggle(c.id)}
                  className={`w-full flex items-center justify-between px-2.5 py-2 rounded text-left transition-colors ${
                    isSelected
                      ? "bg-indigo-50 border border-indigo-200"
                      : "hover:bg-slate-50 border border-transparent"
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-slate-900 truncate">
                      {c.name || c.email}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{c.email}</p>
                  </div>
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                    isSelected
                      ? "bg-indigo-600 border-indigo-600"
                      : "border-slate-300"
                  }`}>
                    {isSelected && (
                      <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-white">
                        <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {selected.size > 0 && (
          <button
            type="button"
            onClick={commitAdd}
            disabled={posting}
            className="w-full inline-flex items-center justify-center gap-1.5 h-10 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
          >
            {posting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Add {selected.size} participant{selected.size === 1 ? "" : "s"}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
